import { useQuery } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";
import { Fragment, useEffect, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

import type {
  DatosComercial,
  MetricasSetter,
  NevoraDataProvider,
} from "../datos/dataProvider";
import { useAjustes } from "../datos/useAjustes";
import { useEsAdmin } from "../datos/useEsAdmin";
import { useCon36 } from "../datos/useFiltroEtapas";
import { useResponsables } from "../datos/useResponsables";
import { euros, mesDe } from "../dominio/comisiones";
import { rangoMes, sumaPorcentajes, tasa } from "../dominio/equipo";
import type { Comercial, RolComercial } from "../dominio/tipos";
import { formatoImporte } from "../negocios/formato";
import { EmbudoConsultores, EmbudoSetters } from "./EmbudoVisual";
import { PaginaAdmin, SelectorMes } from "./PaginaAdmin";
import { ObjetivosEquipo } from "./ObjetivosEquipo";
import { PasarFichas } from "./PasarFichas";

/** Auditoría 30/10/2026: «Closer» y «Team lead» no hacen nada hasta enero; solo salen si alguien ya lo es. */
const ROLES: RolComercial[] = ["setter"];
const ROLES_FUTUROS: RolComercial[] = ["closer", "team_lead"];
/** 36: «Consultor» (Gabriel): solo consultorías, sin leads (40: con comisión, 10 %). */
const ROL_CONSULTOR: RolComercial = "consultor";
const SIN_ROL = "ninguno";

/**
 * Equipo (S9-S10, Jan y Robert 06-07/10/2026): el embudo de cada setter en el mes
 * (llamadas → citas → consultorías realizadas → propuestas → cierres → cobros) y, debajo,
 * su configuración: rol comercial, si recibe leads, % de reparto, tope de pendientes y
 * comisión propia (vacío = la global de Ajustes). Solo administradores.
 */
export const EquipoPage = () => {
  const translate = useTranslate();
  const esAdmin = useEsAdmin();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const [mes, setMes] = useState(() => mesDe(new Date()));
  const { data: metricas, isPending: cargandoMetricas } = useQuery({
    queryKey: ["nevora", "metricasEquipo", mes],
    queryFn: () => {
      const r = rangoMes(mes);
      return dataProvider.metricasEquipo(r.desde, r.hasta);
    },
    enabled: esAdmin,
  });
  const { data: usuarios, refetch } = useGetList<Comercial>(
    "sales",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "first_name", order: "ASC" },
    },
    { enabled: esAdmin },
  );
  const activos = (usuarios ?? []).filter((u) => !u.disabled);
  const suma = sumaPorcentajes(activos);
  const reciben = activos.some((u) => u.recibe_leads);

  return (
    <PaginaAdmin
      titulo="nevora.equipo.titulo"
      ayuda="nevora.equipo.ayuda"
      acciones={<SelectorMes mes={mes} cambiar={setMes} />}
    >
      <section className="rounded-lg border bg-card p-4 space-y-3">
        <h2 className="titulo-seccion">{translate("nevora.equipo.embudo")}</h2>
        {/* 40 (Robert, 23/10/2026): el embudo de cada uno, visual; la tabla con todo, debajo. */}
        {metricas?.length ? <EmbudoSetters metricas={metricas} /> : null}
        <TablaMetricas metricas={metricas} cargando={cargandoMetricas} />
        <p className="text-xs text-muted-foreground">
          {translate("nevora.equipo.embudo_ayuda")}
        </p>
      </section>

      {/* 47 (Robert, 31/10/2026): objetivos del mes por persona; el Inicio enseña cómo van. */}
      <section className="rounded-lg border bg-card p-4 space-y-3">
        <h2 className="titulo-seccion">
          {translate("nevora.equipo.objetivos.titulo", {
            mes: new Date(`${mes}-01T12:00:00`).toLocaleDateString("es-ES", {
              month: "long",
              year: "numeric",
            }),
          })}
        </h2>
        <ObjetivosEquipo mes={mes} usuarios={activos} />
      </section>

      <section className="rounded-lg border bg-card p-4 space-y-3">
        <h2 className="titulo-seccion">
          {translate("nevora.equipo.consultores")}
        </h2>
        <EmbudoConsultores mes={mes} />
        <p className="text-xs text-muted-foreground">
          {translate("nevora.equipo.consultores_ayuda")}
        </p>
      </section>

      <section className="rounded-lg border bg-card p-4 space-y-3">
        <h2 className="titulo-seccion">
          {translate("nevora.equipo.configuracion")}
        </h2>
        <p className="text-sm text-muted-foreground">
          {translate("nevora.equipo.configuracion_ayuda")}
        </p>
        {reciben && Math.round(suma * 100) !== 10000 ? (
          <p
            className="text-sm font-medium text-amber-800 dark:text-amber-300"
            role="status"
            data-testid="suma-porcentajes"
          >
            {translate("nevora.equipo.suma_porcentajes", { suma })}
          </p>
        ) : null}
        <ul className="space-y-3">
          {activos.map((u) => (
            <FilaComercial
              key={String(u.id)}
              usuario={u}
              teamLeads={activos.filter(
                (x) => x.rol_comercial === "team_lead" && x.id !== u.id,
              )}
              onGuardado={() => refetch()}
            />
          ))}
        </ul>
      </section>
    </PaginaAdmin>
  );
};

const PASOS: (keyof MetricasSetter)[] = [
  "llamadas",
  "citas",
  "consultorias",
  "propuestas",
  "cierres",
  "cobros",
];

const TablaMetricas = ({
  metricas,
  cargando,
}: {
  metricas?: MetricasSetter[];
  cargando: boolean;
}) => {
  const translate = useTranslate();
  const { nombre } = useResponsables();
  if (cargando) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {translate("nevora.equipo.cargando")}
      </p>
    );
  }
  if (!metricas?.length) {
    return (
      <p className="text-sm text-muted-foreground">
        {translate("nevora.equipo.sin_setters")}
      </p>
    );
  }
  const celda = "px-2 py-2 text-right tabular-nums whitespace-nowrap";
  // «Interesados» (28c): cuenta aparte, fuera de la cadena del embudo; solo si la base ya la da.
  const conInteresados = metricas.some((m) => m.interesados != null);
  // 36: «No se presentó», aparte, después de las citas (nunca cuenta como consultoría hecha).
  const conNoShows = metricas.some((m) => m.no_shows != null);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="metricas-equipo">
        <thead>
          <tr className="border-b text-xs text-muted-foreground">
            <th className="px-2 py-2 text-left font-medium">
              {translate("nevora.equipo.persona")}
            </th>
            <th className={`${celda} font-medium`}>
              {translate("nevora.equipo.m.pendientes")}
            </th>
            <th className={`${celda} font-medium`}>
              {translate("nevora.equipo.m.recuperados")}
            </th>
            {PASOS.map((p) => (
              <Fragment key={p}>
                <th className={`${celda} font-medium`}>
                  {translate(`nevora.equipo.m.${p}`)}
                </th>
                {p === "llamadas" && conInteresados ? (
                  <th className={`${celda} font-medium`}>
                    {translate("nevora.equipo.m.interesados")}
                  </th>
                ) : null}
                {p === "citas" && conNoShows ? (
                  <th className={`${celda} font-medium`}>
                    {translate("nevora.equipo.m.no_shows")}
                  </th>
                ) : null}
              </Fragment>
            ))}
            <th className={`${celda} font-medium`}>
              {translate("nevora.equipo.m.cobrado")}
            </th>
            <th className={`${celda} font-medium`}>
              {translate("nevora.equipo.m.comision")}
            </th>
          </tr>
        </thead>
        <tbody>
          {metricas.map((m) => (
            <tr key={String(m.sale_id)} className="border-b last:border-b-0">
              <td className="px-2 py-2 font-medium whitespace-nowrap">
                {nombre(m.sale_id)}
              </td>
              <td className={celda}>{m.pendientes}</td>
              <td className={celda}>{m.recuperados}</td>
              {PASOS.map((p, i) => {
                const anterior = i > 0 ? Number(m[PASOS[i - 1]]) : null;
                const t =
                  anterior != null ? tasa(anterior, Number(m[p])) : null;
                return (
                  <Fragment key={p}>
                    <td className={celda}>
                      {Number(m[p])}
                      {t != null ? (
                        <span className="block text-[11px] text-muted-foreground">
                          {t} %
                        </span>
                      ) : null}
                    </td>
                    {p === "llamadas" && conInteresados ? (
                      <td className={celda} data-testid="interesados">
                        {Number(m.interesados ?? 0)}
                      </td>
                    ) : null}
                    {p === "citas" && conNoShows ? (
                      <td className={celda} data-testid="no-shows">
                        {Number(m.no_shows ?? 0)}
                      </td>
                    ) : null}
                  </Fragment>
                );
              })}
              <td className={celda}>{formatoImporte(Number(m.cobrado))}</td>
              <td className={`${celda} font-medium`}>
                {euros(Number(m.comision))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const aNumero = (s: string): number | null =>
  s.trim() === "" ? null : Number(s.replace(",", "."));

const FilaComercial = ({
  usuario,
  teamLeads,
  onGuardado,
}: {
  usuario: Comercial;
  teamLeads: Comercial[];
  onGuardado: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { ajustes } = useAjustes();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const con36 = useCon36();
  const [rol, setRol] = useState<string>(usuario.rol_comercial ?? SIN_ROL);
  const [recibe, setRecibe] = useState(!!usuario.recibe_leads);
  const [pct, setPct] = useState(String(usuario.porcentaje_reparto ?? ""));
  const [tope, setTope] = useState(String(usuario.tope_pendientes ?? ""));
  const [comision, setComision] = useState(String(usuario.comision_pct ?? ""));
  const [lead, setLead] = useState<string>(
    usuario.team_lead_id != null ? String(usuario.team_lead_id) : SIN_ROL,
  );
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setRol(usuario.rol_comercial ?? SIN_ROL);
    setRecibe(!!usuario.recibe_leads);
    setPct(String(usuario.porcentaje_reparto ?? ""));
    setTope(String(usuario.tope_pendientes ?? ""));
    setComision(String(usuario.comision_pct ?? ""));
    setLead(
      usuario.team_lead_id != null ? String(usuario.team_lead_id) : SIN_ROL,
    );
  }, [usuario]);

  const id = (campo: string) => `${campo}-${usuario.id}`;
  const conRol = rol !== SIN_ROL;
  // 36: el consultor no recibe leads (la base lo fuerza igualmente). 40: sí cobra comisión (10 %).
  const vende = conRol && rol !== ROL_CONSULTOR;
  const esConsultor = rol === ROL_CONSULTOR;
  const roles = [
    ...ROLES,
    ...ROLES_FUTUROS.filter((r) => usuario.rol_comercial === r),
    ...(con36 || usuario.rol_comercial === ROL_CONSULTOR
      ? [ROL_CONSULTOR]
      : []),
  ];

  const guardar = async () => {
    const datos: DatosComercial = {
      rol_comercial: conRol ? (rol as RolComercial) : null,
      recibe_leads: vende && recibe,
      porcentaje_reparto: aNumero(pct),
      tope_pendientes: aNumero(tope),
      comision_pct: aNumero(comision),
      team_lead_id: lead !== SIN_ROL ? (lead as unknown as Identifier) : null,
    };
    const fuera = (n: number | null, max: number) =>
      n != null && (Number.isNaN(n) || n < 0 || n > max);
    if (
      fuera(datos.porcentaje_reparto, 100) ||
      fuera(datos.comision_pct, 100) ||
      fuera(datos.tope_pendientes, 32000)
    ) {
      notify("nevora.equipo.error_numeros", { type: "warning" });
      return;
    }
    if (datos.tope_pendientes != null) {
      datos.tope_pendientes = Math.floor(datos.tope_pendientes);
    }
    setGuardando(true);
    try {
      await dataProvider.configurarComercial(usuario.id, datos);
      notify("nevora.equipo.guardado", { type: "success" });
      onGuardado();
    } catch (e) {
      notify(e instanceof Error ? e.message : "ra.notification.http_error", {
        type: "error",
      });
    } finally {
      setGuardando(false);
    }
  };

  return (
    <li
      className="rounded-md border p-3 space-y-3"
      data-testid={`comercial-${usuario.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">
          {`${usuario.first_name ?? ""} ${usuario.last_name ?? ""}`.trim()}
        </span>
        {usuario.administrator ? (
          <Badge variant="secondary">
            {translate("nevora.usuarios.administrador")}
          </Badge>
        ) : null}
        <span className="text-xs text-muted-foreground">{usuario.email}</span>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6 items-end">
        <div className="space-y-1 lg:col-span-1">
          <Label htmlFor={id("rol")}>{translate("nevora.equipo.rol")}</Label>
          <Select value={rol} onValueChange={setRol}>
            <SelectTrigger id={id("rol")} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SIN_ROL}>
                {translate("nevora.equipo.roles.ninguno")}
              </SelectItem>
              {roles.map((r) => (
                <SelectItem key={r} value={r}>
                  {translate(`nevora.equipo.roles.${r}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 h-9">
          <Switch
            id={id("recibe")}
            checked={vende && recibe}
            disabled={!vende}
            onCheckedChange={setRecibe}
          />
          <Label htmlFor={id("recibe")}>
            {translate("nevora.equipo.recibe_leads")}
          </Label>
        </div>
        <div className="space-y-1">
          <Label htmlFor={id("pct")}>
            {translate("nevora.equipo.porcentaje")}
          </Label>
          <Input
            id={id("pct")}
            inputMode="decimal"
            value={pct}
            disabled={!vende}
            onChange={(e) => setPct(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={id("tope")}>{translate("nevora.equipo.tope")}</Label>
          <Input
            id={id("tope")}
            inputMode="numeric"
            value={tope}
            disabled={!vende}
            placeholder={String(ajustes.topePendientes)}
            onChange={(e) => setTope(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={id("comision")}>
            {translate("nevora.equipo.comision")}
          </Label>
          <Input
            id={id("comision")}
            inputMode="decimal"
            value={comision}
            disabled={!vende && !esConsultor}
            placeholder={String(
              esConsultor ? ajustes.comisionConsultorPct : ajustes.comisionPct,
            )}
            onChange={(e) => setComision(e.target.value)}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={guardando}
          onClick={guardar}
        >
          {translate("nevora.equipo.guardar")}
        </Button>
      </div>
      {vende && teamLeads.length ? (
        <div className="space-y-1 max-w-xs">
          <Label htmlFor={id("lead")}>
            {translate("nevora.equipo.team_lead")}
          </Label>
          <Select value={lead} onValueChange={setLead}>
            <SelectTrigger id={id("lead")} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={SIN_ROL}>
                {translate("nevora.equipo.sin_team_lead")}
              </SelectItem>
              {teamLeads.map((t) => (
                <SelectItem key={String(t.id)} value={String(t.id)}>
                  {`${t.first_name ?? ""} ${t.last_name ?? ""}`.trim()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}
      <PasarFichas usuario={usuario} />
    </li>
  );
};

EquipoPage.path = "/equipo";
